import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Users, FileText, Receipt, TrendingUp, AlertCircle, Car, Wrench, Clock, ArrowRight, BarChart3 } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, AreaChart, Area } from 'recharts';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const QUOTE_STATUS_CLASS = { accepte: 'bg-green-50 text-green-600', brouillon: 'bg-gray-100 text-gray-600' };
const INVOICE_STATUS_CLASS = { payee: 'bg-green-50 text-green-600', partielle: 'bg-amber-50 text-amber-600' };
const getQuoteStatusClass = (status) => QUOTE_STATUS_CLASS[status] || 'bg-amber-50 text-amber-600';
const getInvoiceStatusClass = (status) => INVOICE_STATUS_CLASS[status] || 'bg-red-50 text-red-600';

const StatCard = ({ icon: Icon, label, value, sub, color, onClick }) => (
  <button onClick={onClick} className="bg-white rounded-xl p-5 border border-gray-100 hover:shadow-md transition-shadow text-left w-full" data-testid={`stat-${label}`}>
    <div className="flex items-center justify-between mb-3">
      <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${color}`}>
        <Icon size={20} className="text-white" />
      </div>
      {sub && <span className="text-xs text-gray-400">{sub}</span>}
    </div>
    <p className="text-2xl font-bold text-gray-900">{value}</p>
    <p className="text-sm text-gray-500 mt-1">{label}</p>
  </button>
);

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-white border border-gray-200 rounded-lg shadow-lg p-3 text-xs">
      <p className="font-semibold text-gray-900 mb-1">{label}</p>
      {payload.map((p) => (
        <p key={p.dataKey || p.name} style={{ color: p.color }} className="flex items-center gap-2">
          <span className="w-2 h-2 rounded-full inline-block" style={{ backgroundColor: p.color }} />
          {p.name}: <span className="font-semibold">{typeof p.value === 'number' && p.name === 'CA' ? `${p.value.toLocaleString('fr-FR')}€` : p.value}</span>
        </p>
      ))}
    </div>
  );
};

const RevenueChart = ({ chartData }) => (
  <div className="bg-white rounded-xl border border-gray-100 p-5" data-testid="chart-revenue">
    <div className="flex items-center gap-2 mb-4">
      <BarChart3 size={18} className="text-emerald-500" />
      <h3 className="font-semibold text-gray-900">Chiffre d'affaires mensuel</h3>
    </div>
    <ResponsiveContainer width="100%" height={240}>
      <AreaChart data={chartData}>
        <defs>
          <linearGradient id="revenueGradient" x1="0" y1="0" x2="0" y2="1">
            <stop offset="5%" stopColor="#10b981" stopOpacity={0.3} />
            <stop offset="95%" stopColor="#10b981" stopOpacity={0} />
          </linearGradient>
        </defs>
        <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
        <XAxis dataKey="month" tick={{ fontSize: 11, fill: '#9ca3af' }} axisLine={false} tickLine={false} />
        <YAxis tick={{ fontSize: 11, fill: '#9ca3af' }} axisLine={false} tickLine={false} tickFormatter={v => `${v}€`} />
        <Tooltip content={<CustomTooltip />} />
        <Area type="monotone" dataKey="revenue" name="CA" stroke="#10b981" fill="url(#revenueGradient)" strokeWidth={2.5} />
      </AreaChart>
    </ResponsiveContainer>
  </div>
);

const ActivityChart = ({ chartData }) => (
  <div className="bg-white rounded-xl border border-gray-100 p-5" data-testid="chart-activity">
    <div className="flex items-center gap-2 mb-4">
      <BarChart3 size={18} className="text-blue-500" />
      <h3 className="font-semibold text-gray-900">Activité mensuelle</h3>
    </div>
    <ResponsiveContainer width="100%" height={240}>
      <BarChart data={chartData} barCategoryGap="20%">
        <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
        <XAxis dataKey="month" tick={{ fontSize: 11, fill: '#9ca3af' }} axisLine={false} tickLine={false} />
        <YAxis tick={{ fontSize: 11, fill: '#9ca3af' }} axisLine={false} tickLine={false} allowDecimals={false} />
        <Tooltip content={<CustomTooltip />} />
        <Bar dataKey="quotes" name="Devis" fill="#f59e0b" radius={[4, 4, 0, 0]} />
        <Bar dataKey="invoices" name="Factures" fill="#6366f1" radius={[4, 4, 0, 0]} />
        <Bar dataKey="interventions" name="Interventions" fill="#f97316" radius={[4, 4, 0, 0]} />
      </BarChart>
    </ResponsiveContainer>
    <div className="flex justify-center gap-6 mt-2">
      <span className="flex items-center gap-1.5 text-xs text-gray-500"><span className="w-2.5 h-2.5 rounded-sm bg-amber-500" />Devis</span>
      <span className="flex items-center gap-1.5 text-xs text-gray-500"><span className="w-2.5 h-2.5 rounded-sm bg-indigo-500" />Factures</span>
      <span className="flex items-center gap-1.5 text-xs text-gray-500"><span className="w-2.5 h-2.5 rounded-sm bg-orange-500" />Interventions</span>
    </div>
  </div>
);

export default function CRMDashboardHome({ stats, authHeaders, onNavigate }) {
  const [chartData, setChartData] = useState([]);
  const [chartLoading, setChartLoading] = useState(true);

  useEffect(() => {
    const fetchCharts = async () => {
      try {
        const res = await axios.get(`${API_URL}/api/crm/charts`, { headers: authHeaders });
        setChartData(res.data);
      } catch (err) {
        setChartData([]);
      } finally {
        setChartLoading(false);
      }
    };
    fetchCharts();
  }, [authHeaders]);

  if (!stats) return <div className="flex items-center justify-center h-64"><div className="animate-spin w-8 h-8 border-4 border-[#DC2626] border-t-transparent rounded-full" /></div>;

  return (
    <div className="space-y-6" data-testid="crm-home">
      {/* Stats grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard icon={TrendingUp} label="Chiffre d'affaires" value={`${stats.total_revenue?.toLocaleString('fr-FR')}€`} sub="Total" color="bg-emerald-500" onClick={() => onNavigate('invoices')} />
        <StatCard icon={TrendingUp} label="CA du mois" value={`${stats.monthly_revenue?.toLocaleString('fr-FR')}€`} sub="Ce mois" color="bg-blue-500" onClick={() => onNavigate('invoices')} />
        <StatCard icon={AlertCircle} label="Factures impayées" value={stats.invoices_unpaid} sub={`${stats.unpaid_total?.toLocaleString('fr-FR')}€`} color="bg-red-500" onClick={() => onNavigate('invoices')} />
        <StatCard icon={Users} label="Clients" value={stats.total_clients} color="bg-violet-500" onClick={() => onNavigate('clients')} />
        <StatCard icon={FileText} label="Devis en attente" value={stats.quotes_pending} color="bg-amber-500" onClick={() => onNavigate('quotes')} />
        <StatCard icon={Receipt} label="Factures émises" value={stats.total_invoices} color="bg-indigo-500" onClick={() => onNavigate('invoices')} />
        <StatCard icon={Wrench} label="Interventions" value={stats.interventions_active} sub="En cours" color="bg-orange-500" onClick={() => onNavigate('interventions')} />
        <StatCard icon={Car} label="Véhicules en vente" value={stats.total_vehicles_sale} color="bg-cyan-500" onClick={() => onNavigate('vehicles-sale')} />
      </div>

      {/* Charts */}
      {!chartLoading && chartData.length > 0 && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <RevenueChart chartData={chartData} />
          <ActivityChart chartData={chartData} />
        </div>
      )}

      {/* Two column layout */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Recent quotes */}
        <div className="bg-white rounded-xl border border-gray-100 p-5">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold text-gray-900">Derniers devis</h3>
            <button onClick={() => onNavigate('quotes')} className="text-xs text-[#DC2626] hover:underline flex items-center gap-1">Voir tous <ArrowRight size={12} /></button>
          </div>
          {stats.recent_quotes?.length ? stats.recent_quotes.map(q => (
            <div key={q.id} className="flex items-center justify-between py-2.5 border-b border-gray-50 last:border-0">
              <div>
                <p className="text-sm font-medium text-gray-900">{q.number}</p>
                <p className="text-xs text-gray-500">{q.client_name}</p>
              </div>
              <div className="text-right">
                <p className="text-sm font-semibold">{q.total?.toLocaleString('fr-FR')}€</p>
                <span className={`text-xs px-2 py-0.5 rounded-full ${getQuoteStatusClass(q.status)}`}>{q.status}</span>
              </div>
            </div>
          )) : <p className="text-sm text-gray-400 text-center py-4">Aucun devis</p>}
        </div>

        {/* Recent invoices */}
        <div className="bg-white rounded-xl border border-gray-100 p-5">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold text-gray-900">Dernières factures</h3>
            <button onClick={() => onNavigate('invoices')} className="text-xs text-[#DC2626] hover:underline flex items-center gap-1">Voir toutes <ArrowRight size={12} /></button>
          </div>
          {stats.recent_invoices?.length ? stats.recent_invoices.map(inv => (
            <div key={inv.id} className="flex items-center justify-between py-2.5 border-b border-gray-50 last:border-0">
              <div>
                <p className="text-sm font-medium text-gray-900">{inv.number}</p>
                <p className="text-xs text-gray-500">{inv.client_name}</p>
              </div>
              <div className="text-right">
                <p className="text-sm font-semibold">{inv.total?.toLocaleString('fr-FR')}€</p>
                <span className={`text-xs px-2 py-0.5 rounded-full ${getInvoiceStatusClass(inv.status)}`}>{inv.status}</span>
              </div>
            </div>
          )) : <p className="text-sm text-gray-400 text-center py-4">Aucune facture</p>}
        </div>

        {/* Recent clients */}
        <div className="bg-white rounded-xl border border-gray-100 p-5">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold text-gray-900">Derniers clients</h3>
            <button onClick={() => onNavigate('clients')} className="text-xs text-[#DC2626] hover:underline flex items-center gap-1">Voir tous <ArrowRight size={12} /></button>
          </div>
          {stats.recent_clients?.length ? stats.recent_clients.map(c => (
            <div key={c.id} className="flex items-center justify-between py-2.5 border-b border-gray-50 last:border-0">
              <div>
                <p className="text-sm font-medium text-gray-900">{c.first_name} {c.last_name} {c.company_name}</p>
                <p className="text-xs text-gray-500">{c.phone} {c.email && `| ${c.email}`}</p>
              </div>
              <span className="text-xs text-gray-400">{c.created_at?.slice(0, 10)}</span>
            </div>
          )) : <p className="text-sm text-gray-400 text-center py-4">Aucun client</p>}
        </div>

        {/* Activity */}
        <div className="bg-white rounded-xl border border-gray-100 p-5">
          <h3 className="font-semibold text-gray-900 mb-4">Activité récente</h3>
          {stats.activity?.length ? stats.activity.map((a, i) => (
            <div key={`act-${i}`} className="flex items-start gap-3 py-2.5 border-b border-gray-50 last:border-0">
              <Clock size={14} className="text-gray-400 mt-0.5 flex-shrink-0" />
              <div>
                <p className="text-sm text-gray-700">{a.description}</p>
                <p className="text-xs text-gray-400">{a.created_at?.slice(0, 16).replace('T', ' ')}</p>
              </div>
            </div>
          )) : <p className="text-sm text-gray-400 text-center py-4">Aucune activité</p>}
        </div>
      </div>

      {/* Contact messages */}
      {stats.recent_contacts?.length > 0 && (
        <div className="bg-white rounded-xl border border-gray-100 p-5">
          <h3 className="font-semibold text-gray-900 mb-4">Demandes du site web non lues</h3>
          {stats.recent_contacts.map(c => (
            <div key={c.id} className="flex items-center justify-between py-2.5 border-b border-gray-50 last:border-0">
              <div>
                <p className="text-sm font-medium text-gray-900">{c.name} — {c.subject}</p>
                <p className="text-xs text-gray-500">{c.email} | {c.phone}</p>
              </div>
              <span className="text-xs bg-red-50 text-red-600 px-2 py-0.5 rounded-full">Nouveau</span>
            </div>
          ))}
        </div>
      )}

      {/* Low stock alerts */}
      {stats.low_stock?.length > 0 && (
        <div className="bg-amber-50 rounded-xl border border-amber-200 p-5">
          <h3 className="font-semibold text-amber-800 mb-3 flex items-center gap-2"><AlertCircle size={16} /> Alertes stock bas</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
            {stats.low_stock.map(p => (
              <div key={p.id} className="bg-white rounded-lg p-3 border border-amber-100">
                <p className="text-sm font-medium">{p.name}</p>
                <p className="text-xs text-amber-600">Stock: {p.stock} / Seuil: {p.alert_threshold}</p>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
